/*global chrome*/

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import {
    FETCH_NON_USER_COMPANY,
    GET_AUTHORIZED_COMPANY,
    GET_AUTHORIZED_USERS_INITIAL,
    GET_COMPANY_LIST_CREATOR_FLAG
} from '../../graphql/query';
import Email from '../common/email';
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { FingerPrintHash, signOut } from '../../../utils/extension';
import { Avatar, Box, Button, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import { SELECT_COMPANY_LOGIN } from '../../graphql/mutation';
import Consent from './consent';
import LocalStorage from 'utils/extension/localstorage';

const ChooseCompany = ({ email, isLoading, loading }) => {
    const token = LocalStorage.getAuthToken();
    const localEmail = LocalStorage.getUserEmail();

    const userEmail = email || localEmail || '';

    const [refetch, { data: companyData }] = useLazyQuery(GET_AUTHORIZED_COMPANY, {
        context: {
            extToken: token
        },
        fetchPolicy: 'network-only'
    });

    const { data, error } = useQuery(GET_COMPANY_LIST_CREATOR_FLAG, {
        context: {
            extToken: token
        },
        fetchPolicy: 'network-only'
    });

    const [selectCompanyLogin] = useMutation(SELECT_COMPANY_LOGIN, {
        context: {
            extToken: token
        },
        fetchPolicy: 'network-only'
    });

    const [companyList, setCompanyList] = useState([]);
    const [authorization, setAuthorization] = useState({});
    const [showConsent, setShowConsent] = useState(false);
    const [isNotFromOrganization, setIsNotFromOrganization] = useState(false);

    const companyIds = companyList.map((item) => item._id);

    const { data: authorizedUsers, loading: initialUserLoading } = useQuery(GET_AUTHORIZED_USERS_INITIAL, {
        variables: {
            companyIds
        },
        context: {
            extToken: token
        },
        skip: !companyIds.length > 0,
        fetchPolicy: 'network-only'
    });

    const isMultipleCompany = data?.userCompanyListWithCreatorFlag?.length > 1;

    const memoAuthUsers = useMemo(() => {
        if (authorizedUsers?.getAuthorizedUsersInitial?.length) {
            return authorizedUsers?.getAuthorizedUsersInitial.map((item) => item.companyId);
        }
    }, [authorizedUsers?.getAuthorizedUsersInitial]);

    useEffect(() => {
        (async () => {
            if (data?.userCompanyListWithCreatorFlag?.length === 1) {
                LocalStorage.setSelectedCompany(data?.userCompanyListWithCreatorFlag[0]);
            }
            if (companyData?.getAuthorizedCompany?.success) {
                setAuthorization(companyData?.getAuthorizedCompany ?? {});
                setShowConsent(true);
                loading({ secondaryLoading: false });
            }
            if (isMultipleCompany && !initialUserLoading) {
                loading({ secondaryLoading: false });
            }
        })();
    }, [data, initialUserLoading, companyData, isMultipleCompany, error]);

    useEffect(() => {
        (async () => {
            if (data?.userCompanyListWithCreatorFlag?.length === 1) {
                const companyId = data?.userCompanyListWithCreatorFlag[0]?._id;

                await refetch({
                    variables: {
                        companyId,
                        email: userEmail
                    }
                });

                const fingerprint = await FingerPrintHash();
                const { data: companyData } = await selectCompanyLogin({
                    variables: {
                        companyId,
                        fingerprint: fingerprint
                    }
                });

                const token = companyData?.selectCompanyForLogin?.userData?.token?.accessToken;
                if (token) {
                    LocalStorage.setAuthToken(token);
                }
            }
        })();
    }, [data?.userCompanyListWithCreatorFlag, refetch, userEmail]);

    useEffect(() => {
        (async () => {
            if (data) {
                if (data?.userCompanyListWithCreatorFlag?.length === 0) {
                    loading({ secondaryLoading: false });
                    signOut();
                } else {
                    setCompanyList(data.userCompanyListWithCreatorFlag);
                }
            }
        })();
    }, [data, error]);

    const handleCompanySelect = async (company) => {
        const fingerprint = await FingerPrintHash();
        LocalStorage.setSelectedCompany(company);
        try {
            if (isNotFromOrganization) {
                LocalStorage.setUserType();
            }
            loading({ secondaryLoading: true });
            const { data } = await selectCompanyLogin({
                variables: {
                    companyId: company._id,
                    fingerprint: fingerprint
                }
            });
            const authToken = data?.selectCompanyForLogin?.userData?.token?.accessToken;
            if (authToken) {
                LocalStorage.setAuthToken(authToken);
            }
            const response = await refetch({
                variables: {
                    companyId: company._id,
                    email: userEmail
                },
                context: {
                    extToken: authToken
                }
            });
            const auth = response.data?.getAuthorizedCompany ?? {};
            setAuthorization(auth);
        } catch (error) {
            loading({ secondaryLoading: false });
        }
    };

    const onClickSignOut = () => signOut();

    const onClickChooseButton = () => {
        if (!isMultipleCompany) {
            signOut();
        } else {
            setShowConsent(false);
        }
    };

    if (showConsent) {
        return (
            <Consent authorization={authorization} email={userEmail} isSingleCompany={!isMultipleCompany} onClick={onClickChooseButton} />
        );
    }

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%" pb={3}>
            <Box gap={2} display="flex" flexDirection="column">
                <Email email={email} />
                <Typography className="text-dark-blue font-roboto fw-500 font-base">
                    Please select which company account you would like to sign in to:
                </Typography>
                <PerfectScrollbar
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 16,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        height: '350px',
                        padding: '12px 12px 0px 0px',
                        width: '100%'
                    }}
                >
                    {companyList?.length > 0 && (
                        <List
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                gap: 2,
                                paddingX: 0,
                                marginX: 0
                            }}
                        >
                            {companyList.map((company) => {
                                const isAuthorized = memoAuthUsers?.includes(company._id);
                                return (
                                    <ListItem
                                        key={company._id}
                                        disablePadding
                                        sx={{
                                            width: '100%',
                                            borderRadius: 3,
                                            border: '1px solid #dededf'
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => {
                                                handleCompanySelect(company);
                                                loading({ secondaryLoading: true });
                                            }}
                                            disabled={!isAuthorized}
                                        >
                                            <ListItemAvatar>
                                                <Avatar
                                                    alt={company.legalBusinessName}
                                                    src={company.companyLogo}
                                                    sx={{ height: '48px', width: '48px' }}
                                                />
                                            </ListItemAvatar>
                                            <Box
                                                display="flex"
                                                flexDirection="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <ListItemText primary={company.legalBusinessName} />
                                                <Box
                                                    sx={{
                                                        borderRadius: 1,
                                                        px: 1,
                                                        m: 0,
                                                        backgroundColor: '#EEE'
                                                    }}
                                                >
                                                    {!isAuthorized && (
                                                        <Typography color="text-dark-blue" variant="caption" fontWeight={500}>
                                                            Not set up
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </PerfectScrollbar>
            </Box>
            <Box width="100%">
                <Button
                    disableElevation
                    disableRipple
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    className="alternate-reversed"
                    onClick={onClickSignOut}
                >
                    Sign out
                </Button>
            </Box>
        </Box>
    );
};

export default ChooseCompany;
